import React, { useState } from 'react';
import { Modal, Button } from '../ui';
import { useTranslation } from 'react-i18next';

interface DeleteDeploymentModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** finalBackup n'est transmis que pour un PostgreSQL manage. */
  onConfirm: (finalBackup: boolean) => void;
  deploymentName: string;
  /**
   * Service dont la suppression detruit des donnees, ou null.
   *
   * Trois services le font, pour des raisons differentes : PostgreSQL emporte sa
   * base, Kafka emporte les messages encore presents dans ses topics depuis que
   * son volume est supprime avec le cluster (chart nubi-kafka 0.4.0), et Keycloak
   * emporte une base qui vit sur le PostgreSQL d'un AUTRE deploiement. Seul
   * PostgreSQL sait prendre une sauvegarde finale -- d'ou le discriminant plutot
   * qu'un booleen.
   */
  dataKind: 'postgresql' | 'kafka' | 'keycloak' | 'docuseal' | 'openwebui' | 'managedApp' | null;
  /**
   * Keycloak : nom de la base detruite et du PostgreSQL qui l'heberge. Rien ne
   * permet de les deviner cote console -- le lien n'existe que dans la
   * declaration cote API. Sans eux, l'avertissement resterait abstrait au moment
   * ou il doit etre le plus concret.
   */
  keycloakDatabase?: { database_name: string; host_deployment_nom: string | null } | null;
  loading?: boolean;
}

/**
 * Confirmation de suppression d'un deploiement.
 *
 * Remplace un window.confirm : depuis que la suppression emporte la base et ses
 * volumes, le geste detruit des donnees et la confirmation doit le dire.
 *
 * La case de sauvegarde finale est cochee par defaut, comme la console RDS. La
 * decocher est un choix explicite, pas un oubli.
 */
const DeleteDeploymentModal: React.FC<DeleteDeploymentModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  deploymentName,
  dataKind,
  keycloakDatabase = null,
  loading = false,
}) => {
  const { t } = (useTranslation as any)('deployments');
  const [finalBackup, setFinalBackup] = useState(true);

  const handleClose = () => {
    setFinalBackup(true);
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title={t('deleteModal.title') as string} size="lg">
      <div className="p-6">
        <div className="flex justify-center mb-4">
          <div className="flex items-center justify-center h-12 w-12 rounded-full bg-red-500/12">
            <svg className="h-6 w-6 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
        </div>

        <p className="text-sm text-text-tertiary text-center mb-6">
          {t('deleteModal.question', { name: deploymentName })}
        </p>

        {dataKind && (
          <div className="bg-red-500/12 border border-red-500/20 rounded-lg p-4 mb-4">
            <h4 className="text-sm font-semibold text-red-400 mb-2">
              {t('deleteModal.dataWarningTitle')}
            </h4>
            {/* Les valeurs d'interpolation ne servent qu'a Keycloak ; les autres
                libelles les ignorent simplement. */}
            <ul className="text-xs text-red-400 space-y-1 list-disc list-inside">
              {(['warningCluster', 'warningVolumes', 'warningRetention'] as const).map((suffix) => (
                <li key={suffix}>
                  {t(`deleteModal.${dataKind}.${suffix}`, {
                    database: keycloakDatabase?.database_name ?? '',
                    host: keycloakDatabase?.host_deployment_nom ?? '—',
                  })}
                </li>
              ))}
            </ul>
          </div>
        )}

        {dataKind === 'postgresql' && (
          <label className="flex items-start gap-3 mb-6 cursor-pointer">
            <input
              type="checkbox"
              checked={finalBackup}
              onChange={(e) => setFinalBackup(e.target.checked)}
              className="mt-1"
              disabled={loading}
            />
            <span className="text-sm">
              <span className="font-medium">{t('deleteModal.finalBackupLabel')}</span>
              <span className="block text-xs text-text-tertiary mt-1">
                {finalBackup
                  ? t('deleteModal.finalBackupOnHint')
                  : t('deleteModal.finalBackupOffHint')}
              </span>
            </span>
          </label>
        )}

        <div className="flex justify-end gap-3">
          <Button variant="secondary" onClick={handleClose} disabled={loading}>
            {t('cancel', { ns: 'common' })}
          </Button>
          <Button variant="danger" onClick={() => onConfirm(finalBackup)} disabled={loading}>
            {loading
              ? t('deleteModal.inProgress')
              : t('deleteModal.confirm')}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default DeleteDeploymentModal;
